import { T, useGT, useLocale } from 'gt-react';
import { InternalUiSpecSchema, type ExtensionValue } from '@erp/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatExtensionValue } from './formatExtensionValue';

/** Read-only generated catalog: Card -> Stack -> Field. No email or custom cell specs. */
export function InternalUiRenderer({
  spec,
  values,
  currency,
}: {
  spec: unknown;
  values: Record<string, ExtensionValue>;
  currency?: string;
}) {
  const locale = useLocale();
  const gt = useGT();
  const parsed = InternalUiSpecSchema.safeParse(spec);
  if (!parsed.success)
    return (
      <p role="alert">
        <T context="invalid extension detail view">
          This extension view is invalid.
        </T>
      </p>
    );
  const { root, elements } = parsed.data;
  const card = elements[root];
  if (card.type !== 'Card') return null;
  const stack = elements[card.children[0]];
  if (stack.type !== 'Stack') return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{card.props.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="flex flex-col gap-2">
          {stack.children.map((id) => {
            const field = elements[id];
            if (field.type !== 'Field') return null;
            const key = field.props.value.$state
              .slice('/values/'.length)
              .replace(/~1/g, '/')
              .replace(/~0/g, '~');
            const value = Object.prototype.hasOwnProperty.call(values, key)
              ? values[key]
              : undefined;
            return (
              <div key={id}>
                <dt className="text-xs text-muted-foreground">
                  {field.props.label}
                </dt>
                <dd className="text-sm whitespace-pre-wrap break-words">
                  {formatExtensionValue(value, field.props.fieldType, locale, {
                    currency,
                    yes: gt('Yes', { context: 'extension boolean value' }),
                    no: gt('No', { context: 'extension boolean value' }),
                  })}
                </dd>
              </div>
            );
          })}
        </dl>
      </CardContent>
    </Card>
  );
}
